import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { http } from "@/lib/api";
import { Card } from "@/components/portal/ui";
import { FinalAssessments } from "@/pages/admin/FinalAssessments";
import { ConfirmPayment } from "@/components/admin/ManualPayment";

function OpenTuition({ onPaid }) {
  const [d, setD] = useState(null);
  const load = () => http.get("/admin/academics/tuition/open").then((r) => setD(r.data)).catch(() => setD({ items: [] }));
  useEffect(() => { load(); }, []);
  if (!d) return null;
  return (
    <section className="mt-10" data-testid="open-tuition">
      <h2 className="font-display text-2xl text-[#1A1918] mb-1">Academic Tuition · Awaiting Payment</h2>
      <p className="text-[#6B665E] text-sm mb-4">Unpaid and partially paid tuition. Stripe-paid, Complimentary and fully paid records are not listed and can't be changed here.</p>
      <Card className="p-0">
        {d.items.length === 0 && <div className="p-6 text-center text-sm text-[#6B665E]">No unpaid tuition records.</div>}
        {d.items.map((t) => (
          <div key={`${t.customer_id}-${t.course}`} className="p-4 border-b border-[#F0ECE4] last:border-0 flex flex-wrap justify-between items-center gap-3" data-testid={`open-tuition-${t.course}-${t.customer_id}`}>
            <div><div className="font-medium text-[#1A1918]">{t.name || t.email} · {t.code}</div><div className="text-xs text-[#8A998C]">{t.email} · {t.tuition_label}</div></div>
            <ConfirmPayment sid={t.customer_id} student={t.name || t.email} course={t.course} code={t.code} prices={d.prices[t.course]} manual={t.manual} onSaved={() => { load(); onPaid(); }} testid={`open-tuition-pay-${t.course}-${t.customer_id}`} />
          </div>
        ))}
      </Card>
    </section>
  );
}

export default function StudentRecords() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [sp, setSp] = useSearchParams();
  const tab = sp.get("tab") === "final" ? "final" : "students";
  const [pending, setPending] = useState(null);
  useEffect(() => { http.get("/admin/academics/final-assessments").then((r) => setPending(r.data.pending)).catch(() => {}); }, []);
  const loadAll = () => http.get("/admin/academics/students").then((r) => setD(r.data)).catch(() => setD(false));
  useEffect(() => { loadAll(); }, []);
  if (d === null) return <div className="text-[#6B665E]">Loading…</div>;
  if (!d) return <div className="text-[#6B665E]">Could not load student records.</div>;
  const rows = d.students.filter((s) => `${s.name} ${s.email}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <div data-testid="student-records">
      <div className="eyebrow text-[#8A998C] mb-1">Academics</div>
      <h1 className="font-display text-3xl text-[#1A1918] mb-1">Student Records</h1>
      <p className="text-[#6B665E] text-sm mb-5">{d.total} registered students. Status: Not Enrolled (free account, no tuition owed) or Enrolled. Interest-list membership is shown separately and is not an enrollment.</p>
      <div className="flex gap-2 mb-5 border-b border-[#E5E1D8]" data-testid="student-records-tabs">
        {[["students", "All Students"], ["final", `Final Assessments${pending ? ` · ${pending} pending` : ""}`]].map(([k, l]) => (
          <button key={k} onClick={() => setSp(k === "final" ? { tab: "final" } : {})} data-testid={`tab-${k}`}
            className={`px-4 py-2 text-sm -mb-px border-b-2 ${tab === k ? "border-[#9F634E] text-[#1A1918] font-medium" : "border-transparent text-[#6B665E] hover:text-[#1A1918]"}`}>{l}</button>))}
      </div>
      {tab === "final" ? <FinalAssessments initialOpen={sp.get("open")} onLoad={setPending} /> : <>
      <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or email" data-testid="students-search"
        className="w-full sm:w-80 mb-4 border border-[#E5E1D8] rounded-full px-4 py-2 text-sm outline-none focus:border-[#9F634E]/50" />
      <Card className="p-0 overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[#8A998C] border-b border-[#F0ECE4]">
            {["Student", "Registered", "Status", "Tuition", "IS-101 progress", "Sleep Observation Tracker", "Consulting", ""].map((h) => <th key={h} className="p-4 font-medium">{h}</th>)}
          </tr></thead>
          <tbody>
            {rows.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-[#6B665E]">No students found.</td></tr>}
            {rows.map((s) => (
              <tr key={s.id} className="border-b border-[#F0ECE4] last:border-0" data-testid={`student-row-${s.id}`}>
                <td className="p-4"><div className="font-medium text-[#1A1918]">{s.name || "—"}</div><div className="text-xs text-[#8A998C]">{s.email}</div></td>
                <td className="p-4 text-[#6B665E]">{s.created_at ? new Date(s.created_at).toLocaleDateString() : "—"}</td>
                <td className="p-4"><span className={`text-xs rounded-full px-2.5 py-1 ${s.status === "Not Enrolled" ? "bg-[#F3EFE9] text-[#9F634E]" : "bg-[#EEF2EE] text-[#56705B]"}`} data-testid={`student-status-${s.id}`}>{s.status}</span>
                  {s.interest_courses.length > 0 && <div className="text-xs text-[#8A998C] mt-1">Interest list: {s.interest_courses.join(", ")}</div>}</td>
                <td className="p-4 text-xs text-[#6B665E]" data-testid={`student-tuition-${s.id}`}>{s.tuition.length ? s.tuition.map((t) => <div key={t}>{t}</div>) : "—"}</td>
                <td className="p-4">{s.is101_completed}/{s.is101_total} lessons</td>
                <td className="p-4">{s.sleep_journal_entries} entries</td>
                <td className="p-4 text-[#6B665E]">{s.has_consulting_record ? <Link to={`/operator/customer/${s.id}`} data-testid={`student-consulting-${s.id}`} className="hover:underline">Separate record →</Link> : "—"}</td>
                <td className="p-4 whitespace-nowrap"><Link to={`/admin/students/${s.id}`} data-testid={`student-open-${s.id}`} className="text-[#9F634E] hover:underline">Open academic profile →</Link></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
      <OpenTuition onPaid={loadAll} />
      </>}
    </div>
  );
}
